import { useState, useCallback, useRef } from 'react'
import {
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
  type DragStartEvent,
  type DragMoveEvent,
  type DragEndEvent,
} from '@dnd-kit/core'
import type { NotebookNote } from '../../shared/types/notebook'
import type { DropPosition } from './dndTree'
import { computeDropPosition, isDescendantOf } from './dndTree'

export interface TreeDndState {
  activeDragId: string | null
  dropTargetId: string | null
  dropPosition: DropPosition | null
}

interface UseTreeDndOptions {
  notes: NotebookNote[]
  onMoveNote: (
    noteId: string,
    newParentId: string | null,
    newSortOrder: number,
  ) => void
  expandedIds: string[]
  onExpand: (id: string) => void
}

export function useTreeDnd({ notes, onMoveNote, expandedIds, onExpand }: UseTreeDndOptions) {
  const [dndState, setDndState] = useState<TreeDndState>({
    activeDragId: null,
    dropTargetId: null,
    dropPosition: null,
  })

  const dropStateRef = useRef<{ dropTargetId: string | null; dropPosition: DropPosition | null }>({
    dropTargetId: null,
    dropPosition: null,
  })
  const autoExpandTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const lastMoveOverRef = useRef<string | null>(null)

  const pointerSensor = useSensor(PointerSensor, {
    activationConstraint: { distance: 5 },
  })
  const touchSensor = useSensor(TouchSensor, {
    activationConstraint: { delay: 250, tolerance: 5 },
  })
  const sensors = useSensors(pointerSensor, touchSensor)

  const handleDragStart = useCallback((event: DragStartEvent) => {
    dropStateRef.current = { dropTargetId: null, dropPosition: null }
    lastMoveOverRef.current = null
    setDndState({
      activeDragId: String(event.active.id),
      dropTargetId: null,
      dropPosition: null,
    })
  }, [])

  // Fires on every pointer move — continuously updates drop position
  const handleDragMove = useCallback((event: DragMoveEvent) => {
    const { active, over } = event
    if (!over || active.id === over.id) {
      if (dropStateRef.current.dropTargetId !== null) {
        dropStateRef.current = { dropTargetId: null, dropPosition: null }
        setDndState(prev => ({ ...prev, dropTargetId: null, dropPosition: null }))
      }
      if (autoExpandTimerRef.current) {
        clearTimeout(autoExpandTimerRef.current)
        autoExpandTimerRef.current = null
      }
      lastMoveOverRef.current = null
      return
    }

    const draggedId = String(active.id)
    const overId = String(over.id)

    // Prevent dropping onto self or descendants
    if (isDescendantOf(notes, overId, draggedId) || draggedId === overId) {
      if (dropStateRef.current.dropTargetId !== null) {
        dropStateRef.current = { dropTargetId: null, dropPosition: null }
        setDndState(prev => ({ ...prev, dropTargetId: null, dropPosition: null }))
      }
      return
    }

    // Compute drop position from cursor Y
    const overRect = over.rect
    const deltaY = event.delta?.y ?? 0
    let cursorY: number
    if (event.activatorEvent instanceof MouseEvent) {
      cursorY = event.activatorEvent.clientY + deltaY
    } else if (event.activatorEvent instanceof TouchEvent) {
      cursorY = (event.activatorEvent.touches[0]?.clientY ?? overRect.top) + deltaY
    } else {
      cursorY = overRect.top + overRect.height / 2
    }

    const rect = {
      top: overRect.top,
      height: overRect.height,
    } as DOMRect
    const position = computeDropPosition(cursorY, rect)

    // Only update React state if position actually changed (perf optimization)
    if (dropStateRef.current.dropTargetId !== overId || dropStateRef.current.dropPosition !== position) {
      dropStateRef.current = { dropTargetId: overId, dropPosition: position }
      setDndState(prev => ({ ...prev, dropTargetId: overId, dropPosition: position }))
    }

    // Auto-expand: detect when over changes to a new item
    if (overId !== lastMoveOverRef.current) {
      lastMoveOverRef.current = overId
      if (autoExpandTimerRef.current) {
        clearTimeout(autoExpandTimerRef.current)
        autoExpandTimerRef.current = null
      }
      if (position === 'inside' && !expandedIds.includes(overId)) {
        autoExpandTimerRef.current = setTimeout(() => onExpand(overId), 500)
      }
    } else if (position === 'inside' && !autoExpandTimerRef.current && !expandedIds.includes(overId)) {
      // Cursor moved back into 'inside' zone on the same item
      autoExpandTimerRef.current = setTimeout(() => onExpand(overId), 500)
    } else if (position !== 'inside' && autoExpandTimerRef.current) {
      // Cursor moved away from 'inside' zone
      clearTimeout(autoExpandTimerRef.current)
      autoExpandTimerRef.current = null
    }
  }, [notes, expandedIds, onExpand])

  const handleDragEnd = useCallback((event: DragEndEvent) => {
    if (autoExpandTimerRef.current) {
      clearTimeout(autoExpandTimerRef.current)
      autoExpandTimerRef.current = null
    }
    lastMoveOverRef.current = null

    const { active, over } = event
    const { dropTargetId, dropPosition } = dropStateRef.current

    // Clear visual state
    dropStateRef.current = { dropTargetId: null, dropPosition: null }
    setDndState({ activeDragId: null, dropTargetId: null, dropPosition: null })

    if (!over || !dropTargetId || !dropPosition || active.id === over.id) return

    const draggedId = String(active.id)
    const targetNote = notes.find(n => n.id === dropTargetId)
    if (!targetNote) return

    // Prevent dropping onto self or descendants
    if (isDescendantOf(notes, dropTargetId, draggedId)) return

    if (dropPosition === 'inside') {
      // Nest inside target — put at end of target's children
      const targetChildren = notes
        .filter(n => n.parentId === dropTargetId)
        .sort((a, b) => a.sortOrder - b.sortOrder)
      const lastChild = targetChildren[targetChildren.length - 1]
      const lastOrder = lastChild ? lastChild.sortOrder : -1000
      onMoveNote(draggedId, dropTargetId, lastOrder + 1000)
    } else {
      // Reorder: before or after the target
      const newParentId = targetNote.parentId
      const siblings = notes
        .filter(n => n.parentId === newParentId && n.id !== draggedId)
        .sort((a, b) => a.sortOrder - b.sortOrder)

      const targetIndex = siblings.findIndex(n => n.id === dropTargetId)
      if (targetIndex === -1) return

      const target = siblings[targetIndex]!
      let prevOrder: number | null = null
      let nextOrder: number | null = null

      if (dropPosition === 'before') {
        const prev = siblings[targetIndex - 1]
        prevOrder = prev ? prev.sortOrder : null
        nextOrder = target.sortOrder
      } else {
        // after
        const next = siblings[targetIndex + 1]
        prevOrder = target.sortOrder
        nextOrder = next ? next.sortOrder : null
      }

      let newSortOrder: number
      if (prevOrder === null && nextOrder === null) {
        newSortOrder = 0
      } else if (prevOrder === null) {
        newSortOrder = nextOrder! - 1000
      } else if (nextOrder === null) {
        newSortOrder = prevOrder + 1000
      } else {
        newSortOrder = Math.round((prevOrder + nextOrder) / 2)
      }

      onMoveNote(draggedId, newParentId, newSortOrder)
    }
  }, [notes, onMoveNote])

  const handleDragCancel = useCallback(() => {
    if (autoExpandTimerRef.current) {
      clearTimeout(autoExpandTimerRef.current)
      autoExpandTimerRef.current = null
    }
    lastMoveOverRef.current = null
    dropStateRef.current = { dropTargetId: null, dropPosition: null }
    setDndState({ activeDragId: null, dropTargetId: null, dropPosition: null })
  }, [])

  return {
    sensors,
    dndState,
    handleDragStart,
    handleDragMove,
    handleDragEnd,
    handleDragCancel,
  }
}
